import React, { useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Space, Table, type InputRef, type TableColumnsType, type TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import "./add_line_good_receive_return.css";
import axios from "axios";
import useSWR from "swr";

interface DataType {
     key: React.Key;
     item_code: string;
     item_name: string;
     case: number;
     uom: string;
     uom_rate: number;
     location: string;
     ref: string;
     do: string;
     receive: number;
     return_qty: number;
     qty: number;
     return: number;
     item_price: number;
     remark: string;
     do_no: string;
     discount_per_item?: string;
}

interface AddLineGoodReceiveReturnProps {
     goodsReceiveNo: string | undefined;
     onCancel: () => void;
     onSelectLines: (selectedRecords: DataType[]) => void;
     existingData: DataType[];
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then((res) => res.data);
const AddLineGoodReceiveReturn: React.FC<AddLineGoodReceiveReturnProps> = ({
     goodsReceiveNo,
     onCancel,
     onSelectLines,
     existingData,
}) => {
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };

     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const searchInput = useRef<InputRef>(null);
     const { data, isLoading: loading } = useSWR<DataType[]>(
          goodsReceiveNo ? `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-details/${goodsReceiveNo}` : null,
          fetcher
     );
     const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
     const [selectedRows, setSelectedRows] = useState<DataType[]>([]);

     const handleSearch = (
          selectedKeys: string[],
          confirm: FilterDropdownProps["confirm"],
          dataIndex: DataIndex,
     ) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${dataIndex.replace(/_/g, " ")}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) =>
               (record[dataIndex] ?? "")
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const columns: TableColumnsType<DataType> = [
          {
               title: "Item Code",
               dataIndex: "item_code",
               key: "item_code",
               ...getColumnSearchProps("item_code"),
               sorter: (a, b) => a.item_code.localeCompare(b.item_code),
               sortDirections: ["descend", "ascend"],
               onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
          },
          {
               title: "Item Name",
               dataIndex: "item_name",
               key: "item_name",
               ...getColumnSearchProps("item_name"),
               sorter: (a, b) => a.item_name.localeCompare(b.item_name),
               sortDirections: ["descend", "ascend"],
               onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
          },
          {
               title: "Receive",
               dataIndex: "receive",
               key: "receive",
               ...getColumnSearchProps("receive"),
               sorter: (a, b) => a.receive - b.receive,
               sortDirections: ["descend", "ascend"],
               onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
          },
          { title: "Stock Qty", dataIndex: "qty", key: "qty", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "Case", dataIndex: "case", key: "case", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "UOM", dataIndex: "uom", key: "uom", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "UoM Rate", dataIndex: "uom_rate", key: "uom_rate", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "Location", dataIndex: "location", key: "location", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "Ref", dataIndex: "ref", key: "ref", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "DO", dataIndex: "do", key: "do", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "Item Price", dataIndex: "item_price", key: "item_price", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
          { title: "DO No", dataIndex: "do_no", key: "do_no", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
     ];

     const rowSelection = {
          selectedRowKeys,
          onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: DataType[]) => {
               setSelectedRowKeys(newSelectedRowKeys);
               setSelectedRows(newSelectedRows);
          },
          getCheckboxProps: (record: DataType) => {
               const sisa = Number(record.receive || 0) - Number(record.return_qty || 0);
               return {
                    disabled: sisa < 1 || existingData.some(item => item.item_code === record.item_code),
                    name: record.item_name,
               };
          },
     };

     const handleLocalCancel = () => {
          setSelectedRowKeys([]);
          setSelectedRows([]);
          onCancel();
     };

     const handleOk = () => {
          onSelectLines(selectedRows);
          handleLocalCancel();
     };

     return (
          <div id="ieml" style={transformStyle}>
               <div className="gjs-grid-row" id="idnv">
                    <div className="gjs-grid-column" id="iwuo">
                         <Table<DataType>
                              columns={columns}
                              dataSource={data?.filter(item => (Number(item.receive || 0) - Number(item.return_qty || 0)) > 0)}
                              loading={loading}
                              rowSelection={rowSelection}
                              scroll={{ x: 'max-content' }}
                         />
                         <div style={{ marginTop: "20px", textAlign: "right" }}>
                              <Space>
                                   <Button
                                        type="primary"
                                        onClick={handleOk}
                                        style={{
                                             height: "40px",
                                             width: "90px",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}>
                                        OK
                                   </Button>
                                   <Button
                                        type="default"
                                        onClick={handleLocalCancel}
                                        style={{
                                             height: "40px",
                                             width: "90px",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}>
                                        Cancel
                                   </Button>
                              </Space>
                         </div>
                    </div>
               </div>
          </div>
     );
};

export default AddLineGoodReceiveReturn;